import React, { useState, useEffect, useRef } from 'react';
import { useSeasonsStore } from '../../stores/useSeasonsStore';
import { useUIStore } from '../../stores/useUIStore';
import { getValidMonthsForSeason, parseSeasonId, validateEventForSeason } from '../../utils/seasonHelpers';
import Button from '../ui/Button/Button';
import { EventFormFields } from './EventFormFields';
import { DeleteConfirmation } from './DeleteConfirmation';
import { BaseModal } from '../shared/Modal/BaseModal';
import { Event as EventType } from '../../types';
import './EventActionsModal.css';

interface EventActionsModalProps {
  isOpen: boolean;
  event: EventType | null;
  onClose: () => void;
}

export const EventActionsModal: React.FC<EventActionsModalProps> = ({
  isOpen,
  event,
  onClose
}) => {
  const {
    updateEvent,
    deleteEvent,
    getSeasonById,
    eventsBySeason
  } = useSeasonsStore();
  const { showToast } = useUIStore();

  const [eventName, setEventName] = useState('');
  const [eventMonth, setEventMonth] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Component-level state to prevent concurrent operations
  const [isUpdating, setIsUpdating] = useState(false);

  const firstInputRef = useRef<HTMLInputElement>(null);

  const handleClose = () => {
    if (showDeleteConfirm) {
      setShowDeleteConfirm(false);
    } else {
      onClose();
    }
  };

  useEffect(() => {
    if (event && isOpen) {
      setEventName(event.name);
      setEventMonth(event.month);
      setIsEditing(false);
      setShowDeleteConfirm(false);
      setIsUpdating(false);
    }
  }, [event, isOpen]);

  if (!isOpen || !event) {
    return null;
  }

  const season = getSeasonById(event.seasonId);
  const seasonData = season ? parseSeasonId(season.id) : null;

  const handleSubmit = async (formEvent: React.FormEvent) => {
    formEvent.preventDefault();

    if (isUpdating) {
      console.warn('Event update already in progress, skipping');
      return;
    }

    if (!eventName.trim()) {
      showToast('Event name is required', 'error');
      return;
    }

    const allEvents = Object.values(eventsBySeason).flat();
    const seasonContext = {
      seasonName: season?.name || '',
      year: season?.year || new Date().getFullYear()
    };
    const validationResult = validateEventForSeason(
      { name: eventName.trim(), month: eventMonth },
      seasonContext,
      allEvents,
      event.id
    );

    if (!validationResult.isValid) {
      showToast(validationResult.error!, 'error');
      return;
    }

    setIsUpdating(true);

    try {
      await updateEvent(event.id, {
        name: eventName.trim(),
        month: eventMonth
      });
      showToast('Event updated successfully', 'success');
      setIsEditing(false);
      onClose();
    } catch (error) {
      const errorMessage = (error as Error).message;

      if (errorMessage.includes('already exists') || errorMessage.includes('DUPLICATE_EVENT_NAME')) {
        showToast('An event with this name already exists', 'error');
      } else if (errorMessage.includes('not found')) {
        showToast('Event not found. It may have been deleted.', 'error');
        onClose();
      } else if (errorMessage.includes('INVALID_MONTH')) {
        showToast('Invalid month for this season', 'error');
      } else if (errorMessage.includes('Recent activity detected')) {
        showToast('System is busy. Please try again in a moment.', 'error');
      } else {
        showToast(`Failed to update event: ${errorMessage}`, 'error');
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (isUpdating) {
      return;
    }

    setIsUpdating(true);
    try {
      await deleteEvent(event.id);
      showToast('Event deleted successfully', 'success');
      onClose();
    } catch (error) {
      showToast(`Failed to delete event: ${(error as Error).message}`, 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  const validMonths = seasonData ? getValidMonthsForSeason(seasonData.name) : [];

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Event Actions"
      className="event-actions-modal"
      initialFocusRef={firstInputRef}
      data-testid="event-actions-modal"
    >
          {!showDeleteConfirm ? (
            <>
              <div className="event-context-display">
                <h4>{event.name}</h4>
                {season && (
                  <p>Season: {seasonData?.name} {seasonData?.year}</p>
                )}
              </div>

              {isEditing ? (
                <form onSubmit={handleSubmit} className="event-edit-form">
                  <EventFormFields
                    name={eventName}
                    month={eventMonth}
                    months={validMonths}
                    disabled={isUpdating}
                    inputIdPrefix="edit-event"
                    onNameChange={setEventName}
                    onMonthChange={setEventMonth}
                    nameInputRef={firstInputRef}
                  />

                  <div className="form-actions">
                    <Button size="action"
                      type="submit"
                      disabled={isUpdating || !eventName.trim()}
                      variant="primary"
                    >
                      {isUpdating ? 'Updating...' : 'Save Changes'}
                    </Button>
                    <Button size="action"
                      type="button"
                      onClick={() => setIsEditing(false)}
                      variant="secondary"
                      disabled={isUpdating}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="event-actions-menu">
                  <Button size="action"
                    onClick={() => setIsEditing(true)}
                    variant="primary"
                    disabled={isUpdating}
                    data-testid="edit-event-button"
                  >
                    ✏️ Edit Event
                  </Button>
                  <Button size="action"
                    onClick={() => setShowDeleteConfirm(true)}
                    variant="danger"
                    disabled={isUpdating}
                    data-testid="delete-event-button"
                  >
                    🗑️ Delete Event
                  </Button>
                </div>
              )}
            </>
          ) : (
            <DeleteConfirmation
              title="Delete Event?"
              prompt={<>Are you sure you want to delete "{event.name}"?</>}
              warning="This action cannot be undone. All photos associated with this event will also be deleted."
              confirmLabel="Yes, Delete Event"
              isDeleting={isUpdating}
              onConfirm={handleDelete}
              onCancel={() => setShowDeleteConfirm(false)}
              showWarningIcon
              confirmTestId="confirm-delete-event-button"
            />
          )}
    </BaseModal>
  );
};
